// OMNI Design System Global Styles for Chakra UI v3
// Mapped from omni_standard_css.css global styles and body configuration

export const omniGlobalStyles = {
  // HTML and body base styles
  'html, body': {
    margin: 0,
    padding: 0,
    fontFamily: 'body',
    fontSize: 'md', // 14px (--body-size)
    fontWeight: 'normal', // --body-weight: normal
    lineHeight: 'normal', // --body-line-height: 1.5
    color: 'gray.900', // --body-color: var(--black)
    backgroundColor: 'gray.100', // --body-background-color: var(--color-pale-grey-two)
    minWidth: '300px', // --body-min-width: 300px
    textRendering: 'optimizeLegibility', // --body-rendering: optimizeLegibility
    overflowX: 'hidden', // --body-overflow-x: hidden
    overflowY: 'scroll', // --body-overflow-y: scroll
  },

  // Typography reset
  'h1, h2, h3, h4, h5, h6': {
    fontFamily: 'heading',
    fontWeight: 'semibold',
    lineHeight: 'shorter',
    margin: 0,
  },

  p: {
    margin: 0,
    marginBottom: '1rem',
  },

  // Link styles
  a: {
    color: 'brand.500',
    textDecoration: 'none',
    transition: 'color 200ms ease',

    '&:hover': {
      color: 'brand.600',
      textDecoration: 'underline',
    },

    '&:focus': {
      outline: '2px solid',
      outlineColor: 'brand.500',
      outlineOffset: '2px',
    },
  },

  // Form element base styles
  'input, textarea, select': {
    fontFamily: 'body',
    fontSize: 'md',
  },

  // Button reset
  button: {
    fontFamily: 'body',
    cursor: 'pointer',
  },

  // Focus styles for keyboard navigation
  '*:focus': {
    outline: '2px solid',
    outlineColor: 'brand.500',
    outlineOffset: '2px',
  },

  // Remove focus outline for mouse users
  '*:focus:not(:focus-visible)': {
    outline: 'none',
  },

  // Scrollbar styling (webkit browsers)
  '*::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },

  '*::-webkit-scrollbar-track': {
    backgroundColor: 'gray.100',
  },

  '*::-webkit-scrollbar-thumb': {
    backgroundColor: 'gray.400',
    borderRadius: 'base',

    '&:hover': {
      backgroundColor: 'gray.500',
    },
  },

  // Selection styling
  '*::selection': {
    backgroundColor: 'brand.100',
    color: 'brand.800',
  },

  // Image responsiveness
  img: {
    maxWidth: '100%',
    height: 'auto',
  },

  // Table base styles
  table: {
    borderCollapse: 'collapse',
    width: '100%',
  },

  // Code styling
  code: {
    fontFamily: 'mono',
    fontSize: '0.875em',
    backgroundColor: 'gray.100',
    padding: '0.125rem 0.25rem',
    borderRadius: 'sm',
  },

  pre: {
    fontFamily: 'mono',
    fontSize: '0.875em',
    backgroundColor: 'gray.100',
    padding: '1rem',
    borderRadius: 'base',
    overflow: 'auto',

    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },

  // Utility classes that might be used in the app
  '.sr-only': {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },

  '.no-scrollbar': {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',

    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },

  // Loading spinner animation
  '@keyframes spin': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },

  // Fade in animation
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },

  // Slide up animation
  '@keyframes slideUp': {
    from: {
      transform: 'translateY(20px)',
      opacity: 0,
    },
    to: {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },

  // Scale animation
  '@keyframes scale': {
    from: {
      transform: 'scale(0.95)',
      opacity: 0,
    },
    to: {
      transform: 'scale(1)',
      opacity: 1,
    },
  },

  // OMNI specific utilities
  '.omni-gradient-primary': {
    background: 'linear-gradient(135deg, brand.500 0%, secondary.500 100%)',
  },

  '.omni-gradient-navigation': {
    background: 'radial-gradient(circle at 50% 0, navbar.500, navbar.900 89%)',
  },

  '.omni-text-gradient': {
    background: 'linear-gradient(135deg, brand.500 0%, secondary.500 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },

  // Dark mode adjustments (if needed)
  '[data-theme="dark"]': {
    'html, body': {
      backgroundColor: 'navbar.900',
      color: 'gray.100',
    },

    '*::-webkit-scrollbar-track': {
      backgroundColor: 'navbar.800',
    },

    '*::-webkit-scrollbar-thumb': {
      backgroundColor: 'gray.600',

      '&:hover': {
        backgroundColor: 'gray.500',
      },
    },

    code: {
      backgroundColor: 'navbar.700',
      color: 'gray.200',
    },

    pre: {
      backgroundColor: 'navbar.700',
      color: 'gray.200',
    },
  },
};
